import { Avatar } from '@Pages/pageRuntime/components/ui/Avatar';
import { AvatarFallback } from '@Pages/pageRuntime/components/ui/AvatarFallback';
import { AvatarImage } from '@Pages/pageRuntime/components/ui/AvatarImage';
import { Button, type ButtonProps } from '@Pages/pageRuntime/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@Pages/pageRuntime/components/ui/DropdownMenu';
import { SidebarMenuButton } from '@Pages/pageRuntime/components/ui/Sidebar';
import { useIsInSidebar } from '@Pages/pageRuntime/components/ui/useSidebar';
import { usePermissions } from '@Pages/pageRuntime/hooks/usePermissions';
import { logoutCurrentUser } from '@Pages/pageRuntime/lib/logout';
import { usePageRouter } from '@Pages/pageRuntime/pageRuntimeContextValue';
import ChevronsUpDown from 'lucide-react/dist/esm/icons/chevrons-up-down';
import LogOut from 'lucide-react/dist/esm/icons/log-out';

type UserControlProps = {
  /** Additional classes for the trigger button. */
  className?: string;

  /** Button visual style. */
  variant?: ButtonProps['variant'];

  /** Button size preset. */
  size?: ButtonProps['size'];
};

/**
 * Renders a direct logout action for the current identity. Inside a sidebar it renders
 * as a menu row with a tooltip when collapsed to icons (matching the built-in app
 * sidebar); elsewhere as a plain button. Hidden when the page is viewed anonymously.
 *
 * @example
 * <Sdk.Components.LogoutButton />
 */
export function LogoutButton({ className, variant = 'ghost', size = 'default' }: UserControlProps) {
  const { user } = usePermissions();
  const inSidebar = useIsInSidebar();
  const router = usePageRouter();

  if (!user) {
    return null;
  }

  if (inSidebar) {
    return (
      <SidebarMenuButton
        tooltip="Log out"
        className={className}
        onClick={() => logoutCurrentUser(router)}
      >
        <LogOut className="size-4" />
        <span>Log out</span>
      </SidebarMenuButton>
    );
  }

  return (
    <Button
      className={className}
      variant={variant}
      size={size}
      onClick={() => logoutCurrentUser(router)}
    >
      <LogOut className="size-4" />
      <span>Log out</span>
    </Button>
  );
}

/**
 * Renders an account dropdown for the current identity with a logout action. Hidden when
 * the page is viewed anonymously.
 *
 * @example
 * <Sdk.Components.UserMenu />
 */
export function UserMenu({ className, variant = 'ghost', size = 'default' }: UserControlProps) {
  const { user } = usePermissions();
  const router = usePageRouter();

  if (!user) {
    return null;
  }

  const name = typeof user.$display_name === 'string' ? user.$display_name : 'Account';
  const email = typeof user.email === 'string' ? user.email : null;
  const rawAvatar = user.avatar;
  const avatar =
    typeof rawAvatar === 'string' && rawAvatar !== '' ? rawAvatar : '/default-avatar.svg';
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className={className} variant={variant} size={size}>
          <Avatar className="size-6 rounded-lg">
            <AvatarImage src={avatar} alt={name} />
            <AvatarFallback className="rounded-lg text-xs">{initials || 'U'}</AvatarFallback>
          </Avatar>
          <span className="truncate">{name}</span>
          <ChevronsUpDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56 rounded-lg">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5 text-sm">
            <span className="font-medium">{name}</span>
            {email ? <span className="text-muted-foreground">{email}</span> : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logoutCurrentUser(router)}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
